export const content = {
  en: {
    nav: {
      items: [
        { id: 'executive', label: 'Executive Summary' },
        { id: 'asset', label: 'The Asset' },
        { id: 'thesis', label: 'Investment Thesis' },
        { id: 'development', label: 'Phase 1' },
        { id: 'expansion', label: 'Expansion' },
        { id: 'revenue', label: 'Business Model' },
        { id: 'operator', label: 'Management' },
        { id: 'founder', label: 'Founder & Vision' },
        { id: 'deal', label: 'Deal Structure' },
        { id: 'returns', label: 'Returns' },
        { id: 'risk', label: 'Risk' },
        { id: 'positioning', label: 'Market' },
        { id: 'longterm', label: 'Long-Term' },
        { id: 'contact', label: 'Contact' }
      ]
    },
    hero: {
      title: "Tenuta Scerni",
      subtitle: "Value-Add Hospitality Real Estate — Phase 1 Investment",
      description: "Acquisition and repositioning of underutilized Tuscan estate into cash-generating hospitality asset"
    },
    executive: {
      title: "Executive Summary",
      intro: "Tenuta Scerni is a value-add real estate repositioning opportunity. The project involves acquiring an underutilized historic estate near Pisa and converting it into an operational luxury hospitality asset.",
      keyPoints: [
        {
          label: "Project Type",
          value: "Hospitality real estate acquisition and repositioning"
        },
        {
          label: "Phase 1 Scope",
          value: "Activation of 25–30 guest accommodations, F&B operations, event spaces, and outdoor amenities"
        },
        {
          label: "Capital Requirement",
          value: "Under technical evaluation (not yet finalized)"
        },
        {
          label: "Revenue Model",
          value: "Hospitality (primary), F&B, events, agricultural products, lifestyle goods"
        },
        {
          label: "Location",
          value: "15–20 minutes from Pisa International Airport"
        },
        {
          label: "Expansion Potential",
          value: "17 existing structures + land for up to 40 additional suites (optional, performance-based)"
        },
        {
          label: "Total Buildout Capacity",
          value: "60–70 keys across all phases"
        }
      ],
      note: "The asset is structurally sound and does not require major reconstruction. Phase 1 focuses on activation and repositioning, not ground-up development. Expansion is optional and contingent on Phase 1 performance."
    },
    asset: {
      title: "The Asset",
      current: {
        title: "Current State",
        description: "The estate comprises a structurally sound historic villa, extensive landscaped gardens, and multiple ancillary buildings. The property has been maintained and does not require major structural reconstruction. Current state: underutilized for contemporary hospitality operations."
      },
      components: {
        title: "Physical Components",
        items: [
          "Main villa: ~2,500 sqm, structurally sound, ready for interior repositioning",
          "Gardens & event spaces: at least 5 distinct zones suitable for weddings and private events",
          "Pool area with adjacent structure, limonaia (lemon house) for conversion",
          "Productive land: olive groves, gardens, potential for additional amenities (tennis court, etc.)"
        ]
      },
      location: {
        title: "Strategic Location",
        description: "15–20 minutes from Pisa International Airport. Direct flight access to major US and European markets. Positioned in Tuscan hills with authentic countryside character, yet highly accessible for international travelers.",
        mapLink: "https://maps.apple/r/-EPuNHRbIgGm4t",
        airportArticle: "https://www.iltirreno.it/pisa/cronaca/2025/10/08/news/aeroporto-di-pisa-l-espansione-e-le-nuove-rotte-per-il-2026-new-york-cina-turchia-e-il-ritorno-di-ita-1.100772885"
      }
    },
    thesis: {
      title: "Investment Thesis",
      points: [
        {
          title: "Asset Mispricing",
          description: "Property price negotiable due to current underutilization and owner motivation. Acquisition timing provides favorable entry relative to comparable repositioned hospitality assets in region."
        },
        {
          title: "Value Creation Through Repositioning",
          description: "Conversion from underutilized estate to operational hospitality asset generates immediate value through repositioning premium. Comparable repositioned properties in Tuscany trade at significant multiples to unrenovated asset values."
        },
        {
          title: "Revenue Model Advantage",
          description: "Hospitality + F&B + agriculture + product sales creates revenue diversification not present in single-use hospitality assets. Multiple revenue streams reduce single-channel dependency and increase operational resilience."
        },
        {
          title: "Structural Market Strength",
          description: "Tuscany demonstrates consistent international demand with limited supply of authentically positioned luxury hospitality. US traveler demand for Italian luxury hospitality remains structurally strong with multi-decade track record."
        }
      ]
    },
    vision: {
      title: "Platform Approach",
      statement: "This project is designed as a platform, not a single asset, with the ability to scale over time.",
      paragraphs: [
        "Tenuta Scerni combines hospitality, agricultural production, and brand development into a scalable real estate platform.",
        "The business model is structured for multi-revenue generation across accommodations, events, food & beverage, and product sales.",
        "Phase 1 establishes operational foundation. Future phases provide controlled expansion based on performance and market conditions."
      ]
    },
    estate: {
      title: "The Estate",
      location: {
        title: "Location",
        description: "Situated in the hills near Pisa, the estate occupies a privileged position—close enough to international access points, yet removed from tourist circuits. The property benefits from proximity to Pisa International Airport (8 km) while maintaining the privacy and character of the Tuscan countryside."
      },
      land: {
        title: "Land & Architecture",
        description: "The historic villa anchors the property, surrounded by olive groves, gardens, and productive land. The architecture carries the understated elegance of traditional Tuscan building—stone, terracotta, proportion, light. These structures provide the foundation for what will become a larger, carefully orchestrated platform."
      },
      context: {
        title: "Territory",
        description: "The estate sits within reach of the Mediterranean coast, cultural centers like Florence and Lucca, and the natural beauty of the Tuscan interior. This positioning allows for both seclusion and strategic connectivity."
      }
    },
    experience: {
      title: "Experience",
      intro: "Tenuta Scerni is being designed to accommodate multiple modes of experience—each calibrated to a different need, yet unified by a common commitment to quality and intention.",
      pillars: [
        {
          title: "Hospitality",
          description: "A limited number of guest accommodations, designed for those who value privacy, authenticity, and a connection to place. This is not volume-based hospitality—it is selective, considered, and rooted in the estate's identity."
        },
        {
          title: "Weddings & Private Events",
          description: "The estate provides a setting for intimate celebrations and gatherings—weddings, family events, and curated occasions. The approach is refined, not commercial. Each event is treated as a singular experience, not a template."
        },
        {
          title: "Cultural Programming",
          description: "Art residencies, exhibitions, music, and cinema. The estate will serve as a platform for cultural initiatives that align with its values—projects that prioritize depth, craft, and meaningful exchange."
        },
        {
          title: "Agricultural Production",
          description: "Olive oil production, seasonal harvests, and land stewardship. The estate's agricultural dimension is both functional and symbolic—connecting guests and residents to the rhythms of the territory."
        },
        {
          title: "Territory & Yachting",
          description: "Curated experiences that extend beyond the property—access to the Tuscan coast, private yachting, and territorial exploration. These offerings are designed for guests who seek to engage with the region at a deeper level."
        }
      ]
    },
    development: {
      title: "Phase 1 — Core Development",
      investment: "Under Technical Evaluation",
      intro: "Phase 1 activates the core hospitality operation. Capital requirement currently under technical review and will be defined upon completion of detailed build-out analysis.",
      scope: [
        {
          title: "Asset Acquisition",
          description: "Purchase of historic estate including villa, gardens, pool area, limonaia, and ancillary structures. Acquisition price under negotiation."
        },
        {
          title: "Repositioning & Activation",
          description: "Interior renovation of main villa. Conversion of existing structures into 25–30 guest accommodations. Activation of F&B operations, event spaces, pool area, and gardens."
        },
        {
          title: "Operational Launch",
          description: "Boutique luxury hospitality model with integrated F&B, events (weddings/private gatherings), and agricultural production. Designed for cash generation upon launch."
        }
      ],
      timeline: "Current plan targets official opening of the estate in early June 2027, subject to finalization of technical plans and acquisition timeline",
      note: "Phase 1 is structured as a standalone, cash-generating operation. Does not require Phase 2/3 execution to succeed."
    },
    expansion: {
      title: "Expansion Potential",
      intro: "The estate includes significant development capacity beyond Phase 1. Expansion is optional, not required for Phase 1 success, and contingent on operational performance.",
      capacity: [
        {
          label: "Existing Structures",
          value: "17 units",
          description: "Additional buildings available for conversion (optional expansion)"
        },
        {
          label: "New Development Capacity",
          value: "Up to 40 suites",
          description: "Potential additional development subject to planning and strategic direction"
        },
        {
          label: "Total Potential",
          value: "60–70 keys",
          description: "Combined Phase 1 + future expansion capacity across all phases"
        }
      ],
      priority: "Phase 1 investors receive priority participation rights in future expansion phases.",
      note: "Expansion is performance-based and does not require upfront capital commitment. Execution timeline: 2–5 years, dependent on Phase 1 results and market conditions. Phase 1 is designed to succeed independently."
    },
    revenueModel: {
      title: "Revenue Model",
      intro: "Revenue is generated across four verticals. Each serves a distinct function within the business model.",
      streams: [
        {
          title: "A. Hospitality",
          description: "Primary revenue driver. Guest accommodations (25–30 rooms Phase 1), private events, weddings, and curated experiences. This vertical monetizes the core asset and anchors the business model.",
          items: [
            "Guest accommodations (25–30 rooms Phase 1)",
            "Private events and weddings (5+ event-ready zones on property)",
            "Curated experiences and packages"
          ]
        },
        {
          title: "B. Food & Beverage",
          description: "Monetization of dining, bar, and event catering. Generates ancillary revenue and enhances hospitality offering. Margin profile higher than room revenue in high-end hospitality. The majority of produce and animal-derived products will be produced on-site at the estate. For items that cannot be produced internally, the project will source local, zero-kilometer products from the surrounding region.",
          items: [
            "On-site dining operations",
            "Aperitivo and bar service",
            "Event catering and private dining"
          ]
        },
        {
          title: "C. Agricultural Production",
          description: "High-margin revenue from estate-produced goods. Olive oil, honey, and seasonal products. Functions as both revenue channel and brand differentiator.",
          items: [
            "Premium olive oil (estate production)",
            "Honey and seasonal agricultural products",
            "Direct sales and selective distribution"
          ]
        },
        {
          title: "D. Product Line & Brand Extension",
          description: "Scalable revenue stream independent of property capacity. Preserves, aperitivo boxes, and potential cosmetics line. Creates distribution beyond on-site sales and extends brand reach.",
          items: [
            "Preserves, sauces, and artisan goods",
            "Aperitivo boxes and beverage products",
            "Future: Natural cosmetics (soaps, fragrances)"
          ]
        }
      ]
    },
    positioning: {
      title: "Strategic Positioning",
      factors: [
        {
          title: "Market Demand",
          description: "Tuscany maintains consistent luxury hospitality demand. Multi-decade track record with US and international travelers. Growing connectivity to US markets via expanding direct flight routes."
        },
        {
          title: "Accessibility",
          description: "15–20 minutes from Pisa International Airport. Direct access to major European cities and transatlantic routes. International reach without sacrificing countryside authenticity."
        },
        {
          title: "Competitive Landscape",
          description: "Limited supply of authentically positioned, investor-grade hospitality assets in the immediate area. Opportunity to capture premium positioning with minimal direct competition."
        }
      ]
    },
    operator: {
      title: "Operator & Management",
      approach: "Founder-led development model with strategic oversight across all phases of acquisition, repositioning, and launch.",
      structure: [
        {
          title: "Alignment",
          description: "Founder-led structure ensuring strong alignment with investors and a clear focus on long-term value creation."
        },
        {
          title: "Strategic Direction",
          description: "The project is guided by a clear vision to create a differentiated asset within the Tuscan landscape, combining hospitality, culture, and land-driven experiences into a cohesive and scalable concept."
        },
        {
          title: "Long-Term Vision",
          description: "The development is not intended as a short-term operation, but as the foundation of a long-term platform with potential for brand expansion and future asset development."
        }
      ]
    },
    founderVision: {
      title: "Founder & Vision",
      content: "The project is founder-led, with direct involvement across concept development, positioning, and execution strategy.\n\nThe vision is to create a new type of destination in Tuscany — not a traditional hospitality asset, but a hybrid ecosystem combining hospitality, culture, agriculture, and curated experiences.\n\nThe founder maintains an active role in shaping the identity of the project, overseeing development decisions, and ensuring coherence between the asset, the brand, and the long-term strategic direction."
    },
    dealStructure: {
      title: "Deal Structure",
      model: "Equity investment vehicle structured for Phase 1 acquisition and development.",
      allocation: [
        {
          category: "Asset Acquisition",
          description: "Purchase of property and land (price currently under negotiation)"
        },
        {
          category: "Renovation & Build-Out",
          description: "Full restoration of main villa, conversion of 25-30 rooms, infrastructure activation"
        },
        {
          category: "Operational Launch",
          description: "FF&E, staffing, marketing, working capital for initial operations"
        }
      ],
      note: "Structure accommodates phased investor participation, with Phase 1 investors receiving priority rights for future expansion participation."
    },
    returns: {
      title: "Returns & Value Creation",
      intro: "Value generation occurs across multiple layers, providing diversified return pathways.",
      layers: [
        {
          title: "Asset Repositioning Value",
          description: "Immediate value creation through conversion of underutilized asset to operational hospitality property. Comparable repositioned estates demonstrate significant valuation premiums."
        },
        {
          title: "Operational Income",
          description: "Cash generation from hospitality operations, F&B, events, and product sales. Phase 1 designed for operational self-sufficiency post-launch."
        },
        {
          title: "Revenue Diversification",
          description: "Multiple income streams (rooms, F&B, agriculture, products) reduce single-channel dependency and create operational resilience."
        },
        {
          title: "Expansion Optionality",
          description: "17 existing units + ~40 suite development capacity provides controlled expansion upside without immediate capital commitment."
        },
        {
          title: "Brand & Platform Value",
          description: "Scalable brand architecture enables product distribution beyond property, creating additional revenue channels and enterprise value."
        }
      ]
    },
    risk: {
      title: "Risk & Mitigation",
      factors: [
        {
          risk: "Development Execution Risk",
          mitigation: "Phased approach limits capital exposure. Phase 1 standalone viability ensures project does not require Phase 2 execution for success. Founder direct oversight throughout development cycle."
        },
        {
          risk: "Market Demand Risk",
          mitigation: "Tuscany demonstrates multi-decade track record of consistent luxury hospitality demand. Location near Pisa airport provides access to established international traveler base. Pre-launch marketing approach to validate demand."
        },
        {
          risk: "Operational Performance Risk",
          mitigation: "Controlled 25-30 room launch (Phase 1) allows operational refinement before expansion. Multi-revenue model reduces dependence on single income channel. Founder operational involvement ensures accountability."
        },
        {
          risk: "Capital Cost Overrun Risk",
          mitigation: "Detailed renovation budgets with contingency allocation. Experienced local contractor relationships. Phased payment structures tied to milestone completion."
        }
      ]
    },
    longterm: {
      title: "Long-Term Vision",
      statement: "This project is designed as a scalable platform, not a single asset.",
      vision: [
        "Phase 2-3 expansion into additional 17 units and new development capacity (~40 suites)",
        "Brand extension through product distribution (olive oil, lifestyle goods) into retail and e-commerce channels",
        "Potential replication model: application of operational playbook to additional properties",
        "Strategic exit optionality: positioned for acquisition by luxury hospitality groups or continuation as independent platform"
      ]
    },
    investment: {
      title: "Investment Structure",
      summary: "Phase 1 represents a controlled entry into a value-add real estate opportunity with multi-dimensional revenue generation and expansion optionality.",
      structure: [
        {
          label: "Phase 1 Investment",
          value: "~€8M",
          breakdown: "Acquisition + renovation + operational setup"
        },
        {
          label: "Revenue Streams",
          value: "4 Verticals",
          breakdown: "Hospitality, F&B, agriculture, product sales"
        },
        {
          label: "Value Creation",
          value: "3 Layers",
          breakdown: "Asset appreciation, operating income, brand development"
        }
      ],
      approach: "Phased development structure limits initial capital exposure while preserving expansion upside. Phase 1 designed for operational self-sufficiency.",
      thesis: "This is a value-add repositioning of an underutilized asset in a high-demand market, with controlled risk and scalable platform potential.",
      cta: "Request Investment Materials"
    },
    philosophy: {
      title: "Philosophy",
      paragraphs: [
        "Tenuta Scerni is a founder-led project. It is guided by a belief that the most compelling destinations are those built with intention, patience, and a willingness to resist the pressures of mass luxury.",
        "The estate will not pursue volume. It will not prioritize trends. It will remain committed to authenticity—to the idea that value is created through depth, not breadth.",
        "This is a project for those who understand that the best experiences are often the quietest, and that true luxury lies not in excess, but in refinement, integrity, and time."
      ]
    },
    contact: {
      title: "Inquiries",
      note: "For information regarding collaboration, investment, or private visits:",
      email: "nicoleinnocenti5@gmail.com",
      phone: "+39 3756543336",
      disclaimer: "Access to the estate is by invitation or prior arrangement."
    }
  },
  it: {
    nav: {
      items: [
        { id: 'executive', label: 'Executive Summary' },
        { id: 'asset', label: 'L\'Asset' },
        { id: 'thesis', label: 'Tesi Investimento' },
        { id: 'development', label: 'Fase 1' },
        { id: 'expansion', label: 'Espansione' },
        { id: 'revenue', label: 'Business Model' },
        { id: 'operator', label: 'Management' },
        { id: 'founder', label: 'Fondatore & Visione' },
        { id: 'deal', label: 'Struttura Deal' },
        { id: 'returns', label: 'Ritorni' },
        { id: 'risk', label: 'Rischio' },
        { id: 'positioning', label: 'Mercato' },
        { id: 'longterm', label: 'Lungo Termine' },
        { id: 'contact', label: 'Contatto' }
      ]
    },
    hero: {
      title: "Tenuta Scerni",
      subtitle: "Real Estate Ospitalità Value-Add — Investimento Fase 1",
      description: "Acquisizione e riposizionamento tenuta toscana sottoutilizzata in asset ospitalità cash-generating"
    },
    executive: {
      title: "Executive Summary",
      intro: "Tenuta Scerni è un'opportunità di riposizionamento immobiliare value-add. Il progetto prevede l'acquisizione di una tenuta storica sottoutilizzata vicino a Pisa e la sua conversione in asset ospitalità luxury operativo.",
      keyPoints: [
        {
          label: "Tipo Progetto",
          value: "Acquisizione e riposizionamento real estate ospitalità"
        },
        {
          label: "Scope Fase 1",
          value: "Attivazione 25–30 sistemazioni, operazioni F&B, spazi eventi e amenità outdoor"
        },
        {
          label: "Fabbisogno Capitale",
          value: "In valutazione tecnica (non ancora finalizzato)"
        },
        {
          label: "Modello Revenue",
          value: "Ospitalità (primario), F&B, eventi, prodotti agricoli, lifestyle goods"
        },
        {
          label: "Posizione",
          value: "15–20 minuti dall'Aeroporto Internazionale di Pisa"
        },
        {
          label: "Potenziale Espansione",
          value: "17 strutture esistenti + terreno per fino a 40 suite aggiuntive (opzionale, basato su performance)"
        },
        {
          label: "Capacità Totale Buildout",
          value: "60–70 chiavi attraverso tutte le fasi"
        }
      ],
      note: "L'asset è strutturalmente solido e non richiede ricostruzione maggiore. La Fase 1 si concentra su attivazione e riposizionamento, non sviluppo ground-up. L'espansione è opzionale e contingente sulla performance Fase 1."
    },
    asset: {
      title: "L'Asset",
      current: {
        title: "Stato Attuale",
        description: "La tenuta comprende una villa storica strutturalmente solida, ampi giardini paesaggistici e molteplici edifici ausiliari. La proprietà è stata mantenuta e non richiede ricostruzione strutturale maggiore. Stato attuale: sottoutilizzata per operazioni ospitalità contemporanea."
      },
      components: {
        title: "Componenti Fisiche",
        items: [
          "Villa principale: ~2.500 mq, strutturalmente solida, pronta per riposizionamento interni",
          "Giardini & spazi eventi: almeno 5 zone distinte adatte per matrimoni ed eventi privati",
          "Area piscina con struttura adiacente, limonaia per conversione",
          "Terreno produttivo: uliveti, giardini, potenziale per amenità aggiuntive (campo tennis, etc.)"
        ]
      },
      location: {
        title: "Posizione Strategica",
        description: "15–20 minuti dall'Aeroporto Internazionale di Pisa. Accesso diretto voli verso principali mercati USA ed europei. Posizionata nelle colline toscane con autentico carattere campestre, ma altamente accessibile per viaggiatori internazionali.",
        mapLink: "https://maps.apple/r/-EPuNHRbIgGm4t",
        airportArticle: "https://www.iltirreno.it/pisa/cronaca/2025/10/08/news/aeroporto-di-pisa-l-espansione-e-le-nuove-rotte-per-il-2026-new-york-cina-turchia-e-il-ritorno-di-ita-1.100772885"
      }
    },
    thesis: {
      title: "Tesi di Investimento",
      points: [
        {
          title: "Mispricing dell'Asset",
          description: "Prezzo della proprietà negoziabile a causa dell'attuale sottoutilizzo e motivazione del proprietario. Il timing di acquisizione fornisce un ingresso favorevole rispetto ad asset ospitalità riposizionati comparabili nella regione."
        },
        {
          title: "Creazione Valore Attraverso Riposizionamento",
          description: "La conversione da tenuta sottoutilizzata ad asset ospitalità operativo genera valore immediato attraverso il premio di riposizionamento. Proprietà riposizionate comparabili in Toscana scambiano a multipli significativi rispetto ai valori degli asset non ristrutturati."
        },
        {
          title: "Vantaggio Modello Revenue",
          description: "Ospitalità + F&B + agricoltura + vendita prodotti crea diversificazione dei ricavi non presente in asset ospitalità mono-uso. Molteplici flussi di ricavo riducono la dipendenza da singolo canale e aumentano la resilienza operativa."
        },
        {
          title: "Forza Strutturale del Mercato",
          description: "La Toscana dimostra domanda internazionale consistente con offerta limitata di ospitalità luxury autenticamente posizionata. La domanda dei viaggiatori USA per ospitalità luxury italiana rimane strutturalmente forte con track record multi-decennale."
        }
      ]
    },
    vision: {
      title: "Approccio Platform",
      statement: "Questo progetto è progettato come piattaforma, non come singolo asset, con capacità di scalare nel tempo.",
      paragraphs: [
        "Tenuta Scerni combina ospitalità, produzione agricola e sviluppo brand in una piattaforma immobiliare scalabile.",
        "Il modello business è strutturato per generazione multi-revenue attraverso alloggi, eventi, food & beverage e vendita prodotti.",
        "La Fase 1 stabilisce le fondamenta operative. Le fasi future forniscono espansione controllata basata su performance e condizioni di mercato."
      ]
    },
    estate: {
      title: "La Tenuta",
      location: {
        title: "Posizione",
        description: "Situata nelle colline vicino a Pisa, la tenuta occupa una posizione privilegiata—abbastanza vicina ai punti di accesso internazionali, ma lontana dai circuiti turistici. La proprietà beneficia della vicinanza all'Aeroporto Internazionale di Pisa (8 km) mantenendo la privacy e il carattere della campagna toscana."
      },
      land: {
        title: "Terra & Architettura",
        description: "La villa storica è il fulcro della proprietà, circondata da uliveti, giardini e terra produttiva. L'architettura porta l'eleganza sobria dell'edilizia toscana tradizionale—pietra, terracotta, proporzione, luce. Queste strutture forniscono le fondamenta per quella che diventerà una piattaforma più ampia e attentamente orchestrata."
      },
      context: {
        title: "Territorio",
        description: "La tenuta si trova a breve distanza dalla costa mediterranea, centri culturali come Firenze e Lucca, e la bellezza naturale dell'interno toscano. Questo posizionamento permette sia isolamento che connettività strategica."
      }
    },
    experience: {
      title: "Esperienza",
      intro: "Tenuta Scerni è progettata per accogliere molteplici modalità di esperienza—ognuna calibrata a una diversa esigenza, ma unite da un comune impegno verso qualità e intenzione.",
      pillars: [
        {
          title: "Ospitalità",
          description: "Un numero limitato di sistemazioni per ospiti, pensate per chi valorizza privacy, autenticità e connessione con il luogo. Non è ospitalità basata sul volume—è selettiva, ponderata e radicata nell'identità della tenuta."
        },
        {
          title: "Matrimoni & Eventi Privati",
          description: "La tenuta offre un contesto per celebrazioni intime e riunioni—matrimoni, eventi familiari e occasioni curate. L'approccio è raffinato, non commerciale. Ogni evento è trattato come esperienza singolare, non un modello."
        },
        {
          title: "Programmazione Culturale",
          description: "Residenze artistiche, mostre, musica e cinema. La tenuta servirà come piattaforma per iniziative culturali allineate ai suoi valori—progetti che privilegiano profondità, mestiere e scambio significativo."
        },
        {
          title: "Produzione Agricola",
          description: "Produzione di olio d'oliva, raccolti stagionali e gestione del territorio. La dimensione agricola della tenuta è sia funzionale che simbolica—connette ospiti e residenti ai ritmi del territorio."
        },
        {
          title: "Territorio & Yachting",
          description: "Esperienze curate che si estendono oltre la proprietà—accesso alla costa toscana, yachting privato ed esplorazione territoriale. Queste offerte sono pensate per ospiti che cercano di impegnarsi con la regione a un livello più profondo."
        }
      ]
    },
    development: {
      title: "Fase 1 — Sviluppo Core",
      investment: "In Valutazione Tecnica",
      intro: "La Fase 1 attiva l'operazione ospitalità core. Fabbisogno capitale attualmente in revisione tecnica e sarà definito al completamento analisi build-out dettagliata.",
      scope: [
        {
          title: "Acquisizione Asset",
          description: "Acquisto tenuta storica inclusi villa, giardini, area piscina, limonaia e strutture ausiliarie. Prezzo acquisizione in negoziazione."
        },
        {
          title: "Riposizionamento & Attivazione",
          description: "Ristrutturazione interni villa principale. Conversione strutture esistenti in 25–30 sistemazioni ospiti. Attivazione operazioni F&B, spazi eventi, area piscina e giardini."
        },
        {
          title: "Lancio Operativo",
          description: "Modello ospitalità boutique luxury con F&B integrato, eventi (matrimoni/eventi privati) e produzione agricola. Progettato per generazione cash al lancio."
        }
      ],
      timeline: "Il piano attuale prevede l'apertura ufficiale della tenuta all'inizio di giugno 2027, soggetto a finalizzazione piani tecnici e timeline acquisizione",
      note: "Fase 1 è strutturata come operazione standalone cash-generating. Non richiede esecuzione Fase 2/3 per successo."
    },
    expansion: {
      title: "Potenziale Espansione",
      intro: "La tenuta include significativa capacità di sviluppo oltre Fase 1. L'espansione è opzionale, non richiesta per successo Fase 1, e contingente su performance operativa.",
      capacity: [
        {
          label: "Strutture Esistenti",
          value: "17 unità",
          description: "Edifici aggiuntivi disponibili per conversione (espansione opzionale)"
        },
        {
          label: "Nuova Capacità Sviluppo",
          value: "Fino a 40 suite",
          description: "Potenziale sviluppo aggiuntivo soggetto a pianificazione e direzione strategica"
        },
        {
          label: "Potenziale Totale",
          value: "60–70 chiavi",
          description: "Capacità combinata Fase 1 + espansione futura attraverso tutte le fasi"
        }
      ],
      priority: "Investitori Fase 1 ricevono diritti partecipazione prioritaria in future fasi espansione.",
      note: "Espansione basata su performance e non richiede impegno capitale upfront. Timeline esecuzione: 2–5 anni, dipendente da risultati Fase 1 e condizioni mercato. Fase 1 progettata per successo indipendente."
    },
    revenueModel: {
      title: "Modello Revenue",
      intro: "Il revenue è generato attraverso quattro verticali. Ognuna serve una funzione distinta nel modello business.",
      streams: [
        {
          title: "A. Ospitalità",
          description: "Driver revenue primario. Sistemazioni ospiti (25–30 camere Fase 1), eventi privati, matrimoni ed esperienze curate. Questa verticale monetizza l'asset core e àncora il modello business.",
          items: [
            "Sistemazioni ospiti (25–30 camere Fase 1)",
            "Eventi privati e matrimoni (5+ zone pronte per eventi sulla proprietà)",
            "Esperienze curate e pacchetti"
          ]
        },
        {
          title: "B. Food & Beverage",
          description: "Monetizzazione di dining, bar e catering eventi. Genera revenue ancillare e migliora l'offerta ospitalità. Profilo margine superiore a revenue camere nell'ospitalità high-end. La maggior parte dei prodotti vegetali e di derivazione animale saranno prodotti internamente nella tenuta. Per ciò che non può essere prodotto internamente, il progetto utilizzerà prodotti a km 0 e locali della regione circostante.",
          items: [
            "Operazioni ristorazione on-site",
            "Servizio aperitivo e bar",
            "Catering eventi e dining privato"
          ]
        },
        {
          title: "C. Produzione Agricola",
          description: "Revenue alto-margine da beni prodotti dalla tenuta. Olio d'oliva, miele e prodotti stagionali. Funziona sia come canale revenue che differenziatore brand.",
          items: [
            "Olio d'oliva premium (produzione tenuta)",
            "Miele e prodotti agricoli stagionali",
            "Vendita diretta e distribuzione selettiva"
          ]
        },
        {
          title: "D. Linea Prodotti & Brand Extension",
          description: "Stream revenue scalabile indipendente da capacità proprietà. Conserve, box aperitivo e potenziale linea cosmetici. Crea distribuzione oltre vendite on-site ed estende reach brand.",
          items: [
            "Conserve, salse e prodotti artigianali",
            "Box aperitivo e prodotti beverage",
            "Futuro: Cosmetici naturali (saponi, fragranze)"
          ]
        }
      ]
    },
    operator: {
      title: "Operatore & Management",
      approach: "Modello di sviluppo guidato dal fondatore con supervisione strategica attraverso tutte le fasi di acquisizione, riposizionamento e lancio.",
      structure: [
        {
          title: "Allineamento",
          description: "Struttura guidata dal fondatore che garantisce forte allineamento con gli investitori e chiaro focus sulla creazione di valore a lungo termine."
        },
        {
          title: "Direzione Strategica",
          description: "Il progetto è guidato da una visione chiara per creare un asset differenziato nel panorama toscano, combinando ospitalità, cultura ed esperienze legate al territorio in un concept coeso e scalabile."
        },
        {
          title: "Visione Lungo Termine",
          description: "Lo sviluppo non è concepito come operazione di breve termine, ma come fondazione di una piattaforma a lungo termine con potenziale per espansione brand e sviluppo asset futuro."
        }
      ]
    },
    founderVision: {
      title: "Fondatore & Visione",
      content: "Il progetto è guidato dal fondatore, con coinvolgimento diretto in sviluppo concept, posizionamento e strategia esecutiva.\n\nLa visione è creare un nuovo tipo di destinazione in Toscana — non un asset ospitalità tradizionale, ma un ecosistema ibrido che combina ospitalità, cultura, agricoltura ed esperienze curate.\n\nIl fondatore mantiene un ruolo attivo nel plasmare l'identità del progetto, supervisionando decisioni di sviluppo e assicurando coerenza tra asset, brand e direzione strategica a lungo termine."
    },
    dealStructure: {
      title: "Struttura Deal",
      model: "Veicolo di investimento azionario strutturato per acquisizione e sviluppo Fase 1.",
      allocation: [
        {
          category: "Acquisizione Asset",
          description: "Acquisto di proprietà e terreno (prezzo attualmente in negoziazione)"
        },
        {
          category: "Ristrutturazione & Build-Out",
          description: "Restauro completo villa principale, conversione 25-30 camere, attivazione infrastrutture"
        },
        {
          category: "Lancio Operativo",
          description: "FF&E, personale, marketing, capitale circolante per operazioni iniziali"
        }
      ],
      note: "La struttura accomoda partecipazione investitori a fasi, con investitori Fase 1 che ricevono diritti prioritari per partecipazione a future espansioni."
    },
    returns: {
      title: "Ritorni & Creazione Valore",
      intro: "La generazione di valore avviene su molteplici livelli, fornendo percorsi di ritorno diversificati.",
      layers: [
        {
          title: "Valore Riposizionamento Asset",
          description: "Creazione valore immediata attraverso conversione asset sottoutilizzato in proprietà ospitalità operativa. Tenute riposizionate comparabili dimostrano premi di valutazione significativi."
        },
        {
          title: "Reddito Operativo",
          description: "Generazione cassa da operazioni ospitalità, F&B, eventi e vendita prodotti. Fase 1 progettata per auto-sufficienza operativa post-lancio."
        },
        {
          title: "Diversificazione Revenue",
          description: "Molteplici flussi di reddito (camere, F&B, agricoltura, prodotti) riducono dipendenza da singolo canale e creano resilienza operativa."
        },
        {
          title: "Opzionalità Espansione",
          description: "17 unità esistenti + ~40 capacità sviluppo suite fornisce upside espansione controllata senza impegno capitale immediato."
        },
        {
          title: "Valore Brand & Platform",
          description: "Architettura brand scalabile abilita distribuzione prodotti oltre la proprietà, creando canali revenue aggiuntivi e valore enterprise."
        }
      ]
    },
    risk: {
      title: "Rischio & Mitigazione",
      factors: [
        {
          risk: "Rischio Esecuzione Sviluppo",
          mitigation: "Approccio a fasi limita esposizione capitale. Fattibilità standalone Fase 1 assicura che progetto non richieda esecuzione Fase 2 per successo. Supervisione diretta fondatore attraverso ciclo sviluppo."
        },
        {
          risk: "Rischio Domanda Mercato",
          mitigation: "La Toscana dimostra track record multi-decennale di domanda ospitalità luxury consistente. Posizione vicino aeroporto Pisa fornisce accesso a base viaggiatori internazionali consolidata. Approccio marketing pre-lancio per validare domanda."
        },
        {
          risk: "Rischio Performance Operativa",
          mitigation: "Lancio controllato 25-30 camere (Fase 1) permette raffinamento operativo prima espansione. Modello multi-revenue riduce dipendenza da singolo canale reddito. Coinvolgimento operativo fondatore assicura accountability."
        },
        {
          risk: "Rischio Superamento Costi Capitale",
          mitigation: "Budget ristrutturazione dettagliati con allocazione contingenza. Relazioni contractor locali esperti. Strutture pagamento a fasi legate a completamento milestone."
        }
      ]
    },
    longterm: {
      title: "Visione Lungo Termine",
      statement: "Questo progetto è progettato come piattaforma scalabile, non come singolo asset.",
      vision: [
        "Espansione Fase 2-3 in 17 unità aggiuntive e nuova capacità sviluppo (~40 suite)",
        "Estensione brand attraverso distribuzione prodotti (olio oliva, beni lifestyle) in canali retail ed e-commerce",
        "Modello replicazione potenziale: applicazione playbook operativo a proprietà aggiuntive",
        "Opzionalità exit strategica: posizionata per acquisizione da gruppi ospitalità luxury o continuazione come piattaforma indipendente"
      ]
    },
    positioning: {
      title: "Posizionamento Strategico",
      factors: [
        {
          title: "Domanda Mercato",
          description: "La Toscana mantiene domanda ospitalità luxury consistente. Track record multi-decennale con viaggiatori US e internazionali. Crescente connettività a mercati US via espansione rotte volo dirette."
        },
        {
          title: "Accessibilità",
          description: "15–20 minuti dall'Aeroporto Internazionale di Pisa. Accesso diretto a principali città europee e rotte transatlantiche. Reach internazionale senza sacrificare autenticità campestre."
        },
        {
          title: "Panorama Competitivo",
          description: "Offerta limitata di asset ospitalità autenticamente posizionati, investor-grade nell'area immediata. Opportunità di catturare posizionamento premium con minima competizione diretta."
        }
      ]
    },
    brandVision: {
      title: "Visione Brand Globale",
      paragraphs: [
        "Tenuta Scerni viene sviluppata sia come destinazione fisica che come piattaforma brand scalabile.",
        "I prodotti derivati dalla tenuta—olio d'oliva, conserve, beni lifestyle—possono essere distribuiti attraverso retail, ospitalità e canali direct-to-consumer.",
        "L'architettura del brand è progettata per espansione internazionale: da singola tenuta a ecosistema lifestyle riconoscibile con applicazioni oltre la Toscana."
      ]
    },
    investment: {
      title: "Struttura Investimento",
      summary: "Fase 1 rappresenta ingresso controllato in opportunità immobiliare value-add con generazione revenue multi-dimensionale e opzionalità espansione.",
      structure: [
        {
          label: "Investimento Fase 1",
          value: "~€8M",
          breakdown: "Acquisizione + ristrutturazione + setup operativo"
        },
        {
          label: "Flussi Revenue",
          value: "4 Verticali",
          breakdown: "Ospitalità, F&B, agricoltura, vendita prodotti"
        },
        {
          label: "Creazione Valore",
          value: "3 Livelli",
          breakdown: "Apprezzamento asset, reddito operativo, sviluppo brand"
        }
      ],
      approach: "Struttura sviluppo a fasi limita esposizione capitale iniziale preservando upside espansione. Fase 1 progettata per auto-sufficienza operativa.",
      thesis: "Questo è un riposizionamento value-add di asset sottoutilizzato in mercato alta domanda, con rischio controllato e potenziale piattaforma scalabile.",
      cta: "Richiedi Materiali Investimento"
    },
    philosophy: {
      title: "Filosofia",
      paragraphs: [
        "Tenuta Scerni è un progetto guidato dal fondatore. È guidato dalla convinzione che le destinazioni più convincenti sono quelle costruite con intenzione, pazienza e volontà di resistere alle pressioni del lusso di massa.",
        "La tenuta non perseguirà il volume. Non darà priorità alle tendenze. Rimarrà impegnata nell'autenticità—nell'idea che il valore si crea attraverso la profondità, non l'ampiezza.",
        "Questo è un progetto per chi comprende che le migliori esperienze sono spesso le più silenziose, e che il vero lusso non risiede nell'eccesso, ma nella raffinatezza, integrità e tempo."
      ]
    },
    contact: {
      title: "Richieste",
      note: "Per informazioni riguardo collaborazioni, investimenti o visite private:",
      email: "nicoleinnocenti5@gmail.com",
      phone: "+39 3756543336",
      disclaimer: "L'accesso alla tenuta è su invito o previo accordo."
    }
  }
};

export const images = {
  logo: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/7kuw6f2w_TENUTA_SCERNI.png",
  hero: {
    video: null, // Client will provide
    placeholder: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/ymiqfc8k_IMG_6610.jpeg"
  },
  vision: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/oyeqo2po_IMG_6609.jpeg",
  estate: {
    main: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/ymiqfc8k_IMG_6610.jpeg",
    architecture: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/yh9d5ivp_IMG_6608.jpeg",
    territory: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/a2l4441m_IMG_6607.jpeg",
    aerial: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/up8u0gog_IMG_6620.jpeg"
  },
  experience: {
    hospitality: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/yh9d5ivp_IMG_6608.jpeg",
    weddings: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/ymiqfc8k_IMG_6610.jpeg",
    culture: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/a2l4441m_IMG_6607.jpeg",
    agriculture: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/oyeqo2po_IMG_6609.jpeg",
    yachting: "https://images.unsplash.com/photo-1773113277395-2be15740dbf3"
  },
  development: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/up8u0gog_IMG_6620.jpeg",
  philosophy: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/oyeqo2po_IMG_6609.jpeg",
  contact: "https://customer-assets.emergentagent.com/job_tuscan-horizon/artifacts/a2l4441m_IMG_6607.jpeg"
};
